import React, { useState, useEffect } from "react";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "../firebase";
import Layout from "../components/Layout";
import { FaSave, FaLink, FaSync, FaExclamationTriangle, FaClock, FaCogs } from "react-icons/fa";

function SystemSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    onlineCollectionUrl: "",
    dayAvgCsvUrl: ""
  });
  const [supabaseUrl, setSupabaseUrl] = useState("");
  const [supabaseKey, setSupabaseKey] = useState("");
  const [message, setMessage] = useState("");
  const [authorized, setAuthorized] = useState(false);
  const [passInput, setPassInput] = useState("");

  // SECURITY CHECK: DDMMYY
  useEffect(() => {
    const now = new Date();
    const d = String(now.getDate()).padStart(2, '0');
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const y = String(now.getFullYear()).slice(-2);
    const correctPassword = `${d}${m}${y}`;
    if (passInput === correctPassword) {
      setAuthorized(true);
    }
  }, [passInput]);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const [sheetSnap, subSnap] = await Promise.all([
        getDoc(doc(db, "settings", "google_sheets")),
        getDoc(doc(db, "settings", "supabase_config"))
      ]);
      if (sheetSnap.exists()) {
        const data = sheetSnap.data();
        setSettings({
          onlineCollectionUrl: data.onlineCollectionUrl || "",
          dayAvgCsvUrl: data.dayAvgCsvUrl || ""
        });
      }
      if (subSnap.exists()) {
        const data = subSnap.data();
        setSupabaseUrl(data.url || "");
        setSupabaseKey(data.anonKey || "");
      }
    } catch (err) {
      console.error("Failed to fetch settings:", err);
    }
    setLoading(false);
  };

  const handleSave = async () => {
    // 🛡️ DYNAMIC PASSWORD PROTECTION (ddmmyy)
    const now = new Date();
    const d = String(now.getDate()).padStart(2, '0');
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const y = String(now.getFullYear()).slice(-2);
    const correctPassword = `${d}${m}${y}`;

    const userInput = prompt("SECURITY CHECK: Enter Daily Authorization Key (ddmmyy):");
    if (userInput !== correctPassword) {
      alert("❌ ACCESS DENIED: Invalid Authorization Key.");
      return;
    }

    setSaving(true);
    setMessage("");
    try {
      await Promise.all([
        setDoc(doc(db, "settings", "google_sheets"), settings),
        setDoc(doc(db, "settings", "supabase_config"), { url: supabaseUrl, anonKey: supabaseKey })
      ]);
      setMessage("Configuration hardened successfully!");
      setTimeout(() => setMessage(""), 4000);
    } catch (err) {
      console.error("Failed to save settings:", err);
      alert("Error saving settings: " + err.message);
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <Layout>
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "80vh" }}>
          <FaSync className="spin" size={40} color="#4f46e5" />
        </div>
      </Layout>
    );
  }

  if (!authorized) {
    return (
      <div style={{ position: "fixed", inset: 0, background: "#0f172a", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, padding: "20px" }}>
        <div style={{ background: "white", padding: "40px", borderRadius: "24px", boxShadow: "0 25px 50px -12px rgba(0,0,0,0.5)", width: "100%", maxWidth: "400px", textAlign: "center" }}>
          <div style={{ width: "60px", height: "60px", background: "#f1f5f9", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
            <FaCogs size={30} color="#4f46e5" />
          </div>
          <h2 style={{ fontSize: "24px", fontWeight: "800", color: "#1e293b", marginBottom: "10px" }}>Security Check</h2>
          <p style={{ color: "#64748b", fontSize: "14px", marginBottom: "25px", fontWeight: "500" }}>Enter the Daily Authorization Key to access the Command Center (DDMMYY).</p>
          <input 
            type="password" 
            placeholder="Enter Key" 
            value={passInput}
            onChange={(e) => setPassInput(e.target.value)}
            maxLength={6}
            style={{ width: "100%", padding: "15px", borderRadius: "12px", border: "2px solid #e2e8f0", fontSize: "20px", textAlign: "center", letterSpacing: "5px", outline: "none", marginBottom: "20px" }}
            autoFocus
          />
          <div style={{ fontSize: "12px", color: "#94a3b8", fontWeight: "600" }}>Hint: Today's date in 6 digits</div>
        </div>
      </div>
    );
  }

  return (
    <Layout>
      <div style={{ padding: "40px", maxWidth: "900px", margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "20px", marginBottom: "40px" }}>
          <div style={{ background: "#4f46e5", color: "white", width: "60px", height: "60px", borderRadius: "18px", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 10px 15px -3px rgba(79, 70, 229, 0.4)" }}>
            <FaCogs size={32} />
          </div>
          <div>
            <h1 style={{ fontSize: "32px", fontWeight: "900", color: "#1e293b", margin: 0 }}>Command Center</h1>
            <p style={{ color: "#64748b", marginTop: "4px", fontSize: "16px", fontWeight: "500" }}>Master control for system operations and data bridges.</p>
          </div>
        </div>

        {message && (
          <div style={{ background: "#f0fdf4", color: "#166534", padding: "16px", borderRadius: "12px", marginBottom: "24px", fontWeight: "700", border: "1px solid #bbf7d0", display: "flex", alignItems: "center", gap: "10px", animation: "slideDown 0.3s ease-out" }}>
            <FaSave /> {message}
          </div>
        )}

        <div style={{ background: "white", borderRadius: "28px", boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.05)", border: "1px solid #f1f5f9", overflow: "hidden" }}>
          <div style={{ padding: "40px" }}>
            
            {/* DATA BRIDGES SECTION */}
            <div style={{ marginBottom: "40px" }}>
              <h2 style={{ fontSize: "20px", fontWeight: "800", color: "#1e293b", marginBottom: "25px", display: "flex", alignItems: "center", gap: "10px" }}>
                <FaLink color="#4f46e5" /> Google Sheet Integrations
              </h2>

              <div style={{ marginBottom: "25px" }}>
                <label style={labelStyle}>Online Collection Bridge (Apps Script URL)</label>
                <span style={{ fontSize: "12px", color: "#4f46e5", display: "block", marginBottom: "10px", fontWeight: "600" }}>
                  Source: Google Apps Script → Deploy → New Deployment → Web App (Set access to 'Anyone')
                </span>
                <input 
                  type="text" 
                  value={settings.onlineCollectionUrl}
                  onChange={(e) => setSettings({...settings, onlineCollectionUrl: e.target.value})}
                  placeholder="https://script.google.com/macros/s/..."
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>Daily Performance CSV URL</label>
                <span style={{ fontSize: "12px", color: "#4f46e5", display: "block", marginBottom: "10px", fontWeight: "600" }}>
                  Source: Google Sheet → File → Share → Publish to Web → Select Tab → Format: CSV
                </span>
                <input 
                  type="text" 
                  value={settings.dayAvgCsvUrl}
                  onChange={(e) => setSettings({...settings, dayAvgCsvUrl: e.target.value})}
                  placeholder="https://docs.google.com/spreadsheets/d/e/..."
                  style={inputStyle}
                />
              </div>
            </div>

            {/* SUPABASE CONFIGURATION */}
            <div style={{ marginBottom: "40px", borderTop: "1px solid #f1f5f9", paddingTop: "30px" }}>
              <h2 style={{ fontSize: "20px", fontWeight: "800", color: "#1e293b", marginBottom: "25px", display: "flex", alignItems: "center", gap: "10px" }}>
                <FaLink color="#4f46e5" /> Supabase Storage Configurations
              </h2>

              <div style={{ marginBottom: "25px" }}>
                <label style={labelStyle}>Supabase Project URL</label>
                <input 
                  type="text" 
                  value={supabaseUrl}
                  onChange={(e) => setSupabaseUrl(e.target.value)}
                  placeholder="https://your-project-id.supabase.co"
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>Supabase Anon Key</label>
                <input 
                  type="text" 
                  value={supabaseKey}
                  onChange={(e) => setSupabaseKey(e.target.value)}
                  placeholder="eyJhbGciOiJIUzI1NiIsInR5cCI6IkpX..."
                  style={inputStyle}
                />
              </div>
            </div>

            {/* AUTOMATION SECTION */}
            <div style={{ marginBottom: "40px", padding: "20px", background: "#f8fafc", borderRadius: "20px", border: "2px dashed #e2e8f0" }}>
              <h2 style={{ fontSize: "18px", fontWeight: "800", color: "#1e293b", marginBottom: "15px", display: "flex", alignItems: "center", gap: "10px" }}>
                <FaClock color="#4f46e5" /> Automated Reports
              </h2>
              <p style={{ fontSize: "14px", color: "#64748b", marginBottom: "20px", fontWeight: "500" }}>
                Manage the daily 5:00 PM Pending Orders email automation.
              </p>
              <button 
                onClick={() => window.location.href = "/settings/pending-report"}
                style={{ padding: "12px 24px", borderRadius: "12px", border: "none", background: "#4f46e5", color: "white", fontWeight: "700", cursor: "pointer", display: "flex", alignItems: "center", gap: "10px" }}
              >
                Configure 5 PM Automation →
              </button>
            </div>

            <div style={{ background: "#fff9c4", padding: "20px", borderRadius: "18px", border: "1px solid #fbc02d", marginBottom: "40px", display: "flex", gap: "15px" }}>
              <FaExclamationTriangle size={28} color="#f57f17" style={{ marginTop: "3px" }} />
              <div>
                <p style={{ margin: 0, fontWeight: "800", color: "#f57f17", fontSize: "15px" }}>Forensic Integrity Warning</p>
                <p style={{ margin: "4px 0 0 0", color: "#a16207", fontSize: "14px", fontWeight: "500", lineHeight: "1.5" }}>
                  Modifying these configurations will instantly redirect live data flows. This action is recorded in the audit logs.
                </p>
              </div>
            </div>

            <button 
              onClick={handleSave}
              disabled={saving}
              style={{ 
                width: "100%", 
                padding: "20px", 
                borderRadius: "20px", 
                border: "none", 
                background: "#1e293b", 
                color: "white", 
                fontSize: "18px", 
                fontWeight: "900", 
                cursor: "pointer", 
                display: "flex", 
                alignItems: "center", 
                justifyContent: "center", 
                gap: "12px",
                transition: "all 0.3s",
                boxShadow: "0 10px 15px -3px rgba(30, 41, 59, 0.4)"
              }}
              onMouseOver={(e) => e.target.style.background = "#0f172a"}
              onMouseOut={(e) => e.target.style.background = "#1e293b"}
            >
              {saving ? <FaSync className="spin" /> : <FaSave />}
              {saving ? "HARDENING CONFIGURATION..." : "SAVE MASTER CONFIGURATION"}
            </button>
          </div>
        </div>
      </div>

      <style>{`
        .spin { animation: spin 1s linear infinite; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes slideDown { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </Layout>
  );
}

const labelStyle = {
  display: "block",
  fontSize: "13px",
  fontWeight: "800",
  color: "#64748b",
  marginBottom: "10px",
  textTransform: "uppercase",
  letterSpacing: "0.05em"
};

const inputStyle = {
  width: "100%",
  padding: "18px",
  borderRadius: "16px",
  border: "2px solid #f1f5f9",
  outline: "none",
  fontSize: "16px",
  fontWeight: "600",
  color: "#1e293b",
  transition: "all 0.2s",
  background: "#f8fafc",
  boxSizing: "border-box"
};

export default SystemSettings;
